

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebaseConfig';
import styled, { keyframes } from 'styled-components';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from "./SEO";
// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components
const BlogPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const BlogHeader = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeIn} 0.6s ease-out;

  h1 {
    font-size: 2.8rem;
    font-weight: 800;
    background: linear-gradient(90deg, #64192E, #64192E);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
  }
`;

const SearchContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  position: relative;

  &:after {
    content: '🔍';
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.6;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.8rem 1.2rem;
  border: 2px solid #e2e8f0;
  border-radius: 50px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: #64192E;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.2);
  }
`;

const BlogPostsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.article`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: both;
  will-change: transform;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
`;

const BlogImageContainer = styled.div`
  height: 200px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);

  @media (max-width: 768px) {
    height: 180px;
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${BlogCard}:hover & {
    transform: scale(1.1);
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgb(211, 126, 150);
  font-weight: 600;
`;

const BlogContent = styled.div`
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const BlogCategory = styled.span`
  background: ${props => props.color || '#e0f2fe'};
  color: ${props => props.textColor || '#0369a1'};
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
`;

const BlogDate = styled.span`
  color: #64748b;
`;

const BlogTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: #1e293b;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BlogExcerpt = styled.p`
  color: #475569;
  margin-bottom: 1.2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: #64192E;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    color: #64192E;
    transform: translateX(5px);

    &:after {
      content: '→';
      margin-left: 5px;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 1rem;
`;

const Spinner = styled.div`
  border: 4px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3b82f6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 1.2rem;
  max-width: 500px;
  line-height: 1.6;
`;

const RetryButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2);
  }
`;

const NoResults = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 3rem;
  color: #64748b;
  font-size: 1.1rem;
  animation: ${pulse} 2s infinite;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  position: relative;

  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 8px;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: #1e293b;
  padding-right: 2rem;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
  padding: 0.5rem;
  z-index: 11;

  &:hover {
    color: #64192E;
  }

  @media (max-width: 768px) {
    top: 0.5rem;
    right: 0.5rem;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    max-height: 200px;
  }
`;

const ModalText = styled.div`
  line-height: 1.8;
  color: #334155;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  p {
    margin-bottom: 1rem;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  bottom: 0;
  background: white;

  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
    flex-direction: column-reverse;
    gap: 0.5rem;
    align-items: flex-end;
  }
`;

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let unsubscribe;

    const fetchBlogs = async () => {
      try {
        if (!db) {
          throw new Error("Firebase database not initialized");
        }

        const blogsRef = ref(db, 'blogs');

        unsubscribe = onValue(blogsRef, (snapshot) => {
          try {
            const data = snapshot.val();

            if (!data) {
              setBlogPosts([]);
              setError('No blog posts found');
              setLoading(false);
              return;
            }

            const posts = Object.entries(data).map(([blogTitle, blogData]) => ({
              id: encodeURIComponent(blogTitle),
              title: blogTitle,
              ...blogData,
              createdAt: blogData.createdAt || new Date().toISOString()
            })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setBlogPosts(posts);
            setError(null);
          } catch (processingError) {
            console.error("Error processing data:", processingError);
            setError('Failed to process blog data');
          } finally {
            setLoading(false);
          }
        }, (firebaseError) => {
          console.error("Firebase read error:", firebaseError);
          setError('Failed to load blog posts. Please try again later.');
          setLoading(false);
        });

      } catch (initializationError) {
        console.error("Initialization error:", initializationError);
        setError('Database connection error');
        setLoading(false);
      }
    };

    fetchBlogs();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const filteredPosts = blogPosts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (post.content && post.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (post.category && post.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleReadMore = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    document.body.style.overflow = 'unset';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'technology': { bg: '#e0f2fe', text: '#0369a1' },
      'design': { bg: '#fce7f3', text: '#9d174d' },
      'business': { bg: '#dcfce7', text: '#166534' },
      'lifestyle': { bg: '#fef3c7', text: '#92400e' },
      'default': { bg: '#e0e7ff', text: '#4338ca' }
    };
    
    const normalizedCategory = category?.toLowerCase();
    return colors[normalizedCategory] || colors['default'];
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
        <p>Loading blog posts...</p>
      </LoadingContainer>
    );
  }

  if (error && !blogPosts.length) {
    return (
      <ErrorContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton onClick={() => window.location.reload()}>
          <span>Retry</span>
        </RetryButton>
      </ErrorContainer>
    );
  }

  return (
    <>
      <SEO
        title="Travel Blog | Pearl Tours & Travels"
        description="Read our latest travel blogs, tourism tips, and destination guides for your next adventure."
        keywords="travel blog, tourism tips, destination guides, Pearl Tours blog, travel advice"
      />
      <Navbar />
      <BlogPageContainer>
        <BlogHeader>
          <h1>Discover Our Blog</h1>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
        </BlogHeader>

        <BlogPostsContainer>
          {filteredPosts.length === 0 ? (
            <NoResults>
              <p>No blog posts found{searchTerm && ` matching "${searchTerm}"`}</p>
            </NoResults>
          ) : (
            filteredPosts.map((post, index) => (
              <BlogCard key={post.id} style={{ animationDelay: `${index * 0.1}s` }}>
                <BlogImageContainer>
                  {post.featuredImage ? (
                    <BlogImage 
                      src={post.featuredImage} 
                      alt={post.title}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Available';
                        e.target.onerror = null;
                      }}
                    />
                  ) : (
                    <ImagePlaceholder>
                      <span>No Image</span>
                    </ImagePlaceholder>
                  )}
                </BlogImageContainer>
                <BlogContent>
                  <BlogMeta>
                    <BlogCategory 
                      color={getCategoryColor(post.category).bg} 
                      textColor={getCategoryColor(post.category).text}
                    >
                      {post.category || 'General'}
                    </BlogCategory>
                    <BlogDate>
                      {new Date(post.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </BlogDate>
                  </BlogMeta>
                  <BlogTitle>{post.title}</BlogTitle>
                  <BlogExcerpt>
                    {post.content?.substring(0, 150) || 'No content available'}
                    {post.content?.length > 150 ? '...' : ''}
                  </BlogExcerpt>
                  <ReadMoreLink 
                    to="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleReadMore(post);
                    }}
                  >
                    Continue Reading
                  </ReadMoreLink>
                </BlogContent>
              </BlogCard>
            ))
          )}
        </BlogPostsContainer>
      </BlogPageContainer>
      <Footer />

      {/* Modal/Popup */}
      {isModalOpen && selectedPost && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedPost.title}</ModalTitle>
              <ModalCloseButton onClick={closeModal}>&times;</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {selectedPost.featuredImage && (
                <ModalImage 
                  src={selectedPost.featuredImage} 
                  alt={selectedPost.title}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Available';
                    e.target.onerror = null;
                  }}
                />
              )}
              <BlogMeta>
                <BlogCategory 
                  color={getCategoryColor(selectedPost.category).bg} 
                  textColor={getCategoryColor(selectedPost.category).text}
                >
                  {selectedPost.category || 'General'}
                </BlogCategory>
                <BlogDate>
                  {new Date(selectedPost.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </BlogDate>
              </BlogMeta>
              <ModalText dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
            </ModalBody>
            <ModalFooter>
              <BlogDate>
                Posted on {new Date(selectedPost.createdAt).toLocaleDateString()}
              </BlogDate>
              {/* <ModalCloseButton onClick={closeModal}>
                Close
              </ModalCloseButton> */}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default BlogPage;